import * as Sentry from "@sentry/vue";
import { getConvexAuthInfo } from "@/v2Projects/convexClient";
import type { ProjectSide } from "@/v2Projects/types";

type SentryLevel = "fatal" | "error" | "warning" | "log" | "info" | "debug";

type SafePrimitive = string | number | boolean | null | undefined;
type SafeExtra = Record<string, SafePrimitive | SafePrimitive[]>;

interface ConvexTelemetryContext {
  operation: string;
  supportReference?: string;
  projectId?: number | null;
  threadId?: string | null;
  side?: ProjectSide | "unknown" | null;
  level?: SentryLevel;
  functionName?: string;
  auth?: {
    hasAccessToken?: boolean;
    hasConvexToken?: boolean;
    forceRefreshToken?: boolean;
    didRecover?: boolean;
    isRecoverable?: boolean;
    recoveryAttempt?: number;
    isAuthenticated?: boolean;
  };
  pagination?: {
    hasCursor?: boolean;
    pageSize?: number;
    isDone?: boolean;
  };
  counts?: {
    messages?: number;
    attachments?: number;
    files?: number;
    notifications?: number;
  };
  extra?: SafeExtra;
}

function compactObject<T extends Record<string, unknown>>(value: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined && entry !== null),
  ) as Partial<T>;
}

function getViewerContext() {
  try {
    const authInfo = getConvexAuthInfo();
    if (!authInfo) {
      return { hasViewer: false };
    }

    return {
      hasViewer: true,
      userId: authInfo.userId,
      orgId: authInfo.orgId,
      isAdmin: authInfo.isAdmin,
    };
  } catch {
    return { hasViewer: false };
  }
}

function buildContext(context: ConvexTelemetryContext) {
  return compactObject({
    operation: context.operation,
    supportReference: context.supportReference,
    projectId: context.projectId ?? undefined,
    threadId: context.threadId ?? undefined,
    side: context.side ?? undefined,
    functionName: context.functionName,
    auth: context.auth ? compactObject(context.auth) : undefined,
    pagination: context.pagination ? compactObject(context.pagination) : undefined,
    counts: context.counts ? compactObject(context.counts) : undefined,
    viewer: getViewerContext(),
    extra: context.extra ? compactObject(context.extra) : undefined,
  });
}

export function createSupportReference(prefix = "SE"): string {
  const random = Math.random().toString(36).slice(2, 8).toUpperCase();
  const timestamp = Date.now().toString(36).slice(-6).toUpperCase();
  return `${prefix}-${timestamp}-${random}`;
}

export function addConvexBreadcrumb(context: ConvexTelemetryContext) {
  Sentry.addBreadcrumb({
    category: "convex.chat",
    level: context.level ?? "info",
    message: context.operation,
    data: buildContext(context),
  });
}

export function captureConvexError(error: unknown, context: ConvexTelemetryContext): string {
  const level = context.level ?? "error";
  const supportReference = context.supportReference ?? createSupportReference();

  Sentry.withScope((scope) => {
    scope.setLevel(level);
    scope.setTag("area", "v2_projects");
    scope.setTag("support.reference", supportReference);
    scope.setTag("convex.operation", context.operation);
    if (context.functionName) {
      scope.setTag("convex.function", context.functionName);
    }
    if (context.side) {
      scope.setTag("project.side", context.side);
    }
    scope.setContext("convex_chat", buildContext({ ...context, supportReference }));

    Sentry.captureException(error instanceof Error ? error : new Error(String(error ?? "Unknown Convex error")));
  });
  return supportReference;
}
